import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Button } from 'antd';
import { GoldOutlined } from '@ant-design/icons';
import moment from 'moment';
import { AssetService } from './asset.service';
import { Asset, ASSET_STATUS_LABELS, Liability } from '../../types';
import { useGlobalContext } from '../../store/GlobalContext';
import DetailPage, {
  DetailFields,
  DetailRelated,
  DetailSection,
  DetailStats,
  Field,
  formatBool,
  formatCurrency,
} from '../../components/DetailPage';

/** The asset endpoint loads its liabilities alongside the portfolio Asset already declares. */
interface AssetWithRelations extends Asset {
  liabilities?: Liability[];
}

const AssetDetails: React.FC = () => {
  const { assetId } = useParams<{ assetId: string }>();
  const navigate = useNavigate();
  const { selectedEstate } = useGlobalContext();
  const [asset, setAsset] = useState<AssetWithRelations | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const load = async () => {
      if (!selectedEstate?.uuid || !assetId) {
        setError('No active estate is selected.');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setAsset(await AssetService.fetchAssetById(selectedEstate.uuid, assetId));
        setError(null);
      } catch (err: any) {
        setError(err.message || 'Failed to load asset');
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [assetId, selectedEstate?.uuid]);

  /* Disposition is stored as a set of independent flags, so the page reports
     the ones that are set rather than pretending there is a single status. */
  const disposition = [
    asset?.sold && 'Sold',
    asset?.donate && 'Donated',
    asset?.givenToFriend && 'Given to a friend',
    asset?.thrownOut && 'Discarded',
  ].filter(Boolean) as string[];

  const tags = [
    asset?.status ? { label: ASSET_STATUS_LABELS[asset.status] ?? asset.status } : null,
    asset?.category ? { label: asset.category, color: 'blue' } : null,
    asset?.jointlyOwned ? { label: 'Jointly owned', color: 'purple' } : null,
    asset?.coveredInWill ? { label: 'In will', color: 'green' } : null,
  ].filter(Boolean) as { label: string; color?: string }[];

  const liabilities = asset?.liabilities || [];
  const portfolio = asset?.portfolio;

  return (
    <DetailPage
      eyebrow="Asset"
      title={asset?.name || 'Asset'}
      icon={<GoldOutlined />}
      tags={tags}
      loading={loading}
      error={error}
      backTo="/assets"
      backLabel="Back to assets"
    >
      <DetailStats
        stats={[
          { label: 'Market value', value: formatCurrency(asset?.marketValue) },
          { label: 'Estimated value', value: formatCurrency(asset?.estimatedValue) },
          { label: 'Lien against asset', value: formatCurrency(asset?.liens) },
          { label: 'Net of lien', value: formatCurrency(asset?.netOfLien) },
          { label: 'Recent statement value', value: formatCurrency(asset?.statementValue) },
        ]}
      />

      <DetailSection title="Overview">
        <DetailFields>
          <Field label="Description" value={asset?.description} span />
          <Field label="Category" value={asset?.category} />
          <Field label="Location" value={asset?.location} />
          <Field label="Reference" value={asset?.ref} />
          <Field label="Item number" value={asset?.itemNumber} />
          <Field label="Secondary identifier" value={asset?.secondaryIdentifier} />
          <Field label="Added" value={asset?.createdAt ? moment(asset.createdAt).format('LL') : null} />
        </DetailFields>
      </DetailSection>

      <DetailSection title="Valuation">
        <DetailFields>
          <Field label="Value type" value={asset?.valueType} />
          <Field label="Value source" value={asset?.valueSource} />
          <Field label="Valuation basis" value={asset?.valuationBasis} />
          <Field label="Original market value" value={formatCurrency(asset?.originalMarketValue)} />
          <Field label="Discount to market" value={formatCurrency(asset?.discountToMarket)} />
          <Field label="Family discount" value={formatCurrency(asset?.familyDiscount)} />
          <Field label="Realized value" value={formatCurrency(asset?.realizedValue)} />
          <Field label="Cash received" value={formatCurrency(asset?.cashReceived)} />
        </DetailFields>
      </DetailSection>

      <DetailSection title="Probate treatment">
        <DetailFields>
          <Field label="Covered in will" value={formatBool(asset?.coveredInWill)} />
          <Field label="EPTL exemption" value={formatBool(asset?.EPTLExemption)} />
          <Field label="EPTL category" value={asset?.eptlCategory} />
          <Field label="Small estate" value={formatBool(asset?.isSmallEstate)} />
          <Field label="Counted in probate" value={formatCurrency(asset?.countedInProbateValue)} />
          <Field label="Jointly owned" value={formatBool(asset?.jointlyOwned)} />
          <Field
            label="Share jointly owned"
            value={
              typeof asset?.percentJointlyOwned === 'number'
                ? `${asset.percentJointlyOwned}%`
                : null
            }
          />
          <Field label="Estate value if in will" value={formatCurrency(asset?.estateValueIfInWill)} />
          <Field label="Disposition" value={disposition.join(', ')} emptyText="Still in the estate" />
        </DetailFields>
      </DetailSection>

      {portfolio && (
        <DetailSection title="Portfolio">
          <DetailFields>
            <Field label="Ticker" value={portfolio.ticker} />
            <Field label="Equity" value={portfolio.equity} />
            <Field label="Cost basis" value={formatCurrency(portfolio.costBasis)} />
            <Field label="Prior day close" value={formatCurrency(portfolio.priorDayClosingPrice)} />
            <Field label="Quantity owned" value={portfolio.currentQuantityOwned} />
            <Field label="Original quantity" value={portfolio.originalQuantityOwned} />
            <Field
              label="Purchased"
              value={portfolio.dateOfPurchase ? moment(portfolio.dateOfPurchase).format('LL') : null}
            />
          </DetailFields>
        </DetailSection>
      )}

      <DetailSection
        title={`Liabilities secured against this asset (${liabilities.length})`}
        empty={!liabilities.length}
        emptyText="No liabilities are secured against this asset"
      >
        <DetailRelated
          items={liabilities.map((liability) => ({
            key: liability.uid,
            name: liability.name,
            meta: liability.source,
            amount: formatCurrency(liability.amount),
            onClick: () => navigate(`/liabilities/${liability.uid}`),
          }))}
        />
      </DetailSection>
    </DetailPage>
  );
};

export default AssetDetails;
